// Global display time zone setting.
//
// One app-wide value that every displayed timestamp reads from. It persists in
// localStorage and syncs across every tile, React root, and same-origin
// iframe/tab through the `storage` event, so changing it in one popover menu
// updates all mounted surfaces.
import { useEffect, useRef, useState } from "react";
import {
  formatUtcOffset,
  getBrowserTimeZone,
  getTimeZoneAbbreviation,
  getTimeZoneOffsetMinutes,
  getTimeZoneSuffix,
  normalizeTimeZone,
  UTC_TIME_ZONE,
} from "./timezone";

// Financial center for each currency: where its primary stock exchange sits.
// The time zone selector offers the centers for the two legs of the active
// currency pair instead of one long fixed list.
const MARKET_TIME_ZONES = {
  US: { name: "USA", city: "New York", timeZone: "America/New_York" },
  EU: { name: "Eurozone", city: "Frankfurt", timeZone: "Europe/Berlin" },
  GB: { name: "UK", city: "London", timeZone: "Europe/London" },
  JP: { name: "Japan", city: "Tokyo", timeZone: "Asia/Tokyo" },
  CH: { name: "Switzerland", city: "Zurich", timeZone: "Europe/Zurich" },
  CA: { name: "Canada", city: "Toronto", timeZone: "America/Toronto" },
  AU: { name: "Australia", city: "Sydney", timeZone: "Australia/Sydney" },
  NZ: { name: "New Zealand", city: "Wellington", timeZone: "Pacific/Auckland" },
  SE: { name: "Sweden", city: "Stockholm", timeZone: "Europe/Stockholm" },
  NO: { name: "Norway", city: "Oslo", timeZone: "Europe/Oslo" },
  DK: { name: "Denmark", city: "Copenhagen", timeZone: "Europe/Copenhagen" },
  IS: { name: "Iceland", city: "Reykjavik", timeZone: "Atlantic/Reykjavik" },
  PL: { name: "Poland", city: "Warsaw", timeZone: "Europe/Warsaw" },
  CZ: { name: "Czechia", city: "Prague", timeZone: "Europe/Prague" },
  HU: { name: "Hungary", city: "Budapest", timeZone: "Europe/Budapest" },
  RO: { name: "Romania", city: "Bucharest", timeZone: "Europe/Bucharest" },
  TR: { name: "Turkey", city: "Istanbul", timeZone: "Europe/Istanbul" },
  RU: { name: "Russia", city: "Moscow", timeZone: "Europe/Moscow" },
  CN: { name: "China", city: "Shanghai", timeZone: "Asia/Shanghai" },
  HK: { name: "Hong Kong", city: "Hong Kong", timeZone: "Asia/Hong_Kong" },
  TW: { name: "Taiwan", city: "Taipei", timeZone: "Asia/Taipei" },
  KR: { name: "South Korea", city: "Seoul", timeZone: "Asia/Seoul" },
  SG: { name: "Singapore", city: "Singapore", timeZone: "Asia/Singapore" },
  IN: { name: "India", city: "Mumbai", timeZone: "Asia/Kolkata" },
  ID: { name: "Indonesia", city: "Jakarta", timeZone: "Asia/Jakarta" },
  MY: { name: "Malaysia", city: "Kuala Lumpur", timeZone: "Asia/Kuala_Lumpur" },
  TH: { name: "Thailand", city: "Bangkok", timeZone: "Asia/Bangkok" },
  PH: { name: "Philippines", city: "Manila", timeZone: "Asia/Manila" },
  VN: { name: "Vietnam", city: "Ho Chi Minh City", timeZone: "Asia/Ho_Chi_Minh" },
  PK: { name: "Pakistan", city: "Karachi", timeZone: "Asia/Karachi" },
  BD: { name: "Bangladesh", city: "Dhaka", timeZone: "Asia/Dhaka" },
  LK: { name: "Sri Lanka", city: "Colombo", timeZone: "Asia/Colombo" },
  IL: { name: "Israel", city: "Tel Aviv", timeZone: "Asia/Jerusalem" },
  AE: { name: "UAE", city: "Dubai", timeZone: "Asia/Dubai" },
  SA: { name: "Saudi Arabia", city: "Riyadh", timeZone: "Asia/Riyadh" },
  QA: { name: "Qatar", city: "Doha", timeZone: "Asia/Qatar" },
  KW: { name: "Kuwait", city: "Kuwait City", timeZone: "Asia/Kuwait" },
  BH: { name: "Bahrain", city: "Manama", timeZone: "Asia/Bahrain" },
  EG: { name: "Egypt", city: "Cairo", timeZone: "Africa/Cairo" },
  ZA: { name: "South Africa", city: "Johannesburg", timeZone: "Africa/Johannesburg" },
  NG: { name: "Nigeria", city: "Lagos", timeZone: "Africa/Lagos" },
  KE: { name: "Kenya", city: "Nairobi", timeZone: "Africa/Nairobi" },
  MX: { name: "Mexico", city: "Mexico City", timeZone: "America/Mexico_City" },
  BR: { name: "Brazil", city: "Sao Paulo", timeZone: "America/Sao_Paulo" },
  AR: { name: "Argentina", city: "Buenos Aires", timeZone: "America/Argentina/Buenos_Aires" },
  CL: { name: "Chile", city: "Santiago", timeZone: "America/Santiago" },
  CO: { name: "Colombia", city: "Bogota", timeZone: "America/Bogota" },
  PE: { name: "Peru", city: "Lima", timeZone: "America/Lima" },
};

type MarketTimeZoneSetting = keyof typeof MARKET_TIME_ZONES;

export type DisplayTimeZoneSetting = "utc" | "local" | MarketTimeZoneSetting;

const CURRENCY_MARKETS: Record<string, MarketTimeZoneSetting> = {
  USD: "US",
  EUR: "EU",
  GBP: "GB",
  JPY: "JP",
  CHF: "CH",
  CAD: "CA",
  AUD: "AU",
  NZD: "NZ",
  SEK: "SE",
  NOK: "NO",
  DKK: "DK",
  ISK: "IS",
  PLN: "PL",
  CZK: "CZ",
  HUF: "HU",
  RON: "RO",
  TRY: "TR",
  RUB: "RU",
  CNY: "CN",
  CNH: "CN",
  HKD: "HK",
  TWD: "TW",
  KRW: "KR",
  SGD: "SG",
  INR: "IN",
  IDR: "ID",
  MYR: "MY",
  THB: "TH",
  PHP: "PH",
  VND: "VN",
  PKR: "PK",
  BDT: "BD",
  LKR: "LK",
  ILS: "IL",
  AED: "AE",
  SAR: "SA",
  QAR: "QA",
  KWD: "KW",
  BHD: "BH",
  EGP: "EG",
  ZAR: "ZA",
  NGN: "NG",
  KES: "KE",
  MXN: "MX",
  BRL: "BR",
  ARS: "AR",
  CLP: "CL",
  COP: "CO",
  PEN: "PE",
};

const DISPLAY_TIME_ZONE_STORAGE_KEY = "display_time_zone";
const DEFAULT_DISPLAY_TIME_ZONE_SETTING: DisplayTimeZoneSetting = "utc";

const isMarketTimeZoneSetting = (
  value: string
): value is MarketTimeZoneSetting =>
  Object.prototype.hasOwnProperty.call(MARKET_TIME_ZONES, value);

const isDisplayTimeZoneSetting = (
  value: unknown
): value is DisplayTimeZoneSetting =>
  typeof value === "string" &&
  (value === "utc" || value === "local" || isMarketTimeZoneSetting(value));

/**
 * Market settings for the two legs of a pair, base first: "GBPINR" ->
 * ["GB", "IN"]. Accepts "EUR/USD"-style input; unknown currencies are skipped.
 */
const getCurrencyPairMarketSettings = (
  currencyPair: string | null | undefined
): MarketTimeZoneSetting[] => {
  const letters = String(currencyPair || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  if (letters.length < 6) return [];

  const markets = [letters.slice(0, 3), letters.slice(3, 6)]
    .map((currency) => CURRENCY_MARKETS[currency])
    .filter((market): market is MarketTimeZoneSetting => Boolean(market));

  return Array.from(new Set(markets));
};

/** IANA zone that timestamps are rendered in for a setting. */
const resolveDisplayTimeZone = (setting: DisplayTimeZoneSetting) => {
  if (setting === "utc") return UTC_TIME_ZONE;
  if (setting === "local") return normalizeTimeZone(getBrowserTimeZone());
  return normalizeTimeZone(MARKET_TIME_ZONES[setting].timeZone);
};

const readStoredSetting = (): DisplayTimeZoneSetting => {
  try {
    const storedValue = window.localStorage.getItem(
      DISPLAY_TIME_ZONE_STORAGE_KEY
    );
    return isDisplayTimeZoneSetting(storedValue)
      ? storedValue
      : DEFAULT_DISPLAY_TIME_ZONE_SETTING;
  } catch {
    return DEFAULT_DISPLAY_TIME_ZONE_SETTING;
  }
};

let currentSetting: DisplayTimeZoneSetting = readStoredSetting();
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const handleStorageEvent = (event: StorageEvent) => {
  // A null key means localStorage.clear() was called.
  if (event.key !== null && event.key !== DISPLAY_TIME_ZONE_STORAGE_KEY) return;

  const storedSetting = readStoredSetting();
  if (storedSetting === currentSetting) return;

  currentSetting = storedSetting;
  notifyListeners();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  if (listeners.size === 1) {
    // Pick up changes made elsewhere while nothing was subscribed.
    currentSetting = readStoredSetting();
    window.addEventListener("storage", handleStorageEvent);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("storage", handleStorageEvent);
    }
  };
};

export const setDisplayTimeZoneSetting = (
  nextSetting: DisplayTimeZoneSetting
) => {
  try {
    window.localStorage.setItem(DISPLAY_TIME_ZONE_STORAGE_KEY, nextSetting);
  } catch {
    // Storage may be unavailable (privacy mode); the in-memory value still
    // drives every surface in this document.
  }

  if (nextSetting === currentSetting) return;

  currentSetting = nextSetting;
  notifyListeners();
};

/**
 * Selector label. Local and market options carry the resolved zone name, e.g.
 * "Local (IST, UTC+5:30)", "India (Mumbai, IST)".
 */
const getDisplayTimeZoneOptionLabel = (
  setting: DisplayTimeZoneSetting,
  nowMs: number
) => {
  if (setting === "utc") return "UTC";

  const timeZone = resolveDisplayTimeZone(setting);
  if (setting === "local") {
    const zoneName = getTimeZoneAbbreviation(timeZone, nowMs) ?? timeZone;
    const offset = formatUtcOffset(getTimeZoneOffsetMinutes(timeZone, nowMs));
    return `Local (${zoneName}, ${offset})`;
  }

  const { name, city } = MARKET_TIME_ZONES[setting];
  const zoneName = getTimeZoneSuffix(timeZone, nowMs);
  return city === name
    ? `${name} (${zoneName})`
    : `${name} (${city}, ${zoneName})`;
};

/**
 * UTC, Local, then the base and quote currency markets of `currencyPair`.
 * `selectedSetting` is kept in the list when the pair doesn't include it (the
 * setting is global and may have been chosen for another pair), so the
 * selector never renders blank.
 */
export const getDisplayTimeZoneOptions = (
  currencyPair?: string | null,
  selectedSetting?: DisplayTimeZoneSetting
) => {
  const nowMs = Date.now();
  const settings: DisplayTimeZoneSetting[] = [
    "utc",
    "local",
    ...getCurrencyPairMarketSettings(currencyPair),
  ];
  if (selectedSetting && !settings.includes(selectedSetting)) {
    settings.push(selectedSetting);
  }

  return settings.map((setting) => ({
    label: getDisplayTimeZoneOptionLabel(setting, nowMs),
    value: setting,
  }));
};

export const useDisplayTimeZone = () => {
  const [setting, setSetting] = useState(currentSetting);

  useEffect(() => {
    const syncSetting = () => setSetting(currentSetting);
    const unsubscribe = subscribe(syncSetting);
    // Catch a change made between this component's render and subscription.
    syncSetting();
    return unsubscribe;
  }, []);

  return { setting, timeZone: resolveDisplayTimeZone(setting) };
};

/**
 * Falls back to UTC when the active pair changes to one that doesn't include
 * the selected market (EURINR on India -> USDJPY resets to UTC). Call from a
 * component that stays mounted while its menu is closed, so pair changes made
 * through context are caught too.
 */
export const useResetDisplayTimeZoneOnPairChange = (
  currencyPair: string | null | undefined
) => {
  const previousPairRef = useRef(currencyPair);

  useEffect(() => {
    if (previousPairRef.current === currencyPair) return;
    previousPairRef.current = currencyPair;

    const setting = currentSetting;
    if (setting === "utc" || setting === "local") return;
    if (!getCurrencyPairMarketSettings(currencyPair).includes(setting)) {
      setDisplayTimeZoneSetting("utc");
    }
  }, [currencyPair]);
};
